import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { LoggerService } from "@shared/logger.service";
import { PersonService } from "@app/services/person.service";
import { ErrorHandlerService } from "@app/shared/error-handler.service";
import { GoalsService } from "@features/goals/services/goals.service";

interface GoalRequest {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  people: string[];
}

interface GoalResponse {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  person: any;
}

@Component({
  selector: "app-goals-form",
  templateUrl: "./goals-form.component.html",
  styleUrls: ["./goals-form.component.css"],
})
export class GoalsFormComponent implements OnInit {
  isSaving = false;
  formGroup: FormGroup;
  people: any[] = [];
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private personService: PersonService,
    private goalsService: GoalsService,
    private toast: ToastrService,
    private logger: LoggerService,
    private errorHandlerService: ErrorHandlerService,
  ) {
    this.formGroup = this.fb.group({
      id: [""],
      title: ["", Validators.required],
      description: [""],
      startDate: [null],
      endDate: [null],
      people: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.params["id"];
    this.findPeople();

    if (id) {
      this.goalsService.findById(id).subscribe({
        next: (response: GoalResponse) => {
          this.formGroup.get("id").patchValue(id);
          this.formGroup.get("title").patchValue(response.title);
          this.formGroup.get("description").patchValue(response.description);
          this.formGroup.get("startDate").patchValue(response.startDate);
          this.formGroup.get("endDate").patchValue(response.endDate);

          this.formGroup.patchValue({
            people: [response.person.id],
          });
        },
        error: (error: Error) => {
          this.isSaving = false;
          this.handleErrors(error);
        },
      });
    }
  }

  onSubmit() {
    this.isSaving = true;
    const id = this.formGroup.get("id").value;
    const goal: GoalRequest = {
      title: this.formGroup.get("title")?.value,
      description: this.formGroup.get("description")?.value,
      startDate: this.formGroup.get("startDate")?.value,
      endDate: this.formGroup.get("endDate")?.value,
      people: this.formGroup.get("people")?.value,
    };

    if (id) {
      // update
      this.updateGoal(id, goal);
      return;
    }

    // create
    this.createGoal(goal);
  }

  private updateGoal(id: string, goal: GoalRequest): void {
    this.goalsService.update(id, goal).subscribe({
      next: () => {
        this.router.navigate(["goals"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success(`🎉 Meta alterada com sucesso!`);
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao salvar a Meta");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  private createGoal(goal: GoalRequest): void {
    this.goalsService.create(goal).subscribe({
      next: () => {
        this.router.navigate(["goals"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success("🎉 Meta salva com sucesso!");
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao alterar a Meta");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  get managers(): any[] {
    return this.people.filter((p) => p.accessType === "Manager");
  }

  get supervisors(): any[] {
    return this.people.filter((p) => p.accessType === "Supervisor");
  }

  get users(): any[] {
    return this.people.filter((p) => p.accessType === "User");
  }

  getSelectedPeople(): string {
    const peopleIds = this.formGroup.get("people")?.value || [];
    return this.people
      .filter((person) => peopleIds.includes(person.id))
      .map((person) => person.name)
      .join(", ");
  }

  private findPeople() {
    this.personService.findAll().subscribe({
      next: (response: any[]) => {
        this.people = response.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
      },
      error: (err) => {
        this.logger.error("Erro para trazer os usuários", err);
      },
    });
  }

  private handleErrors(ex: any): void {
    if (ex.error.errors) {
      ex.error.errors.forEach((element) => {
        this.errorMessage = "Erro interno: " + element.message;
        this.successMessage = null;
        setTimeout(() => {
          this.errorMessage = null;
        }, 5000);
      });
    } else {
      this.toast.error(ex.error.message);
    }
  }
}
